/**
 * Bridge to Nyafaz Salon Flutter WebView native USB microscope.
 * Inline preview overlays the web preview frame — no separate capture popup.
 * Shared by hair insight and enterprise salon skin macro capture.
 */

export function isNyafazNativeMicroscopeAvailable() {
  if (typeof window === 'undefined') return false;
  const n = window.NyafazNative;
  if (!n) return false;
  return (
    typeof n.startScalpInlinePreview === 'function' ||
    typeof n.captureScalpInline === 'function' ||
    typeof n.listUsbCameras === 'function' ||
    typeof n.openScalpCapture === 'function' ||
    typeof n.captureMicroscope === 'function' ||
    typeof n.openMicroscope === 'function'
  );
}

/**
 * Salon / Presentation WebView injects NyafazNative after first paint — wait before macro boot.
 * @param {number} [timeoutMs]
 * @returns {Promise<boolean>}
 */
export function waitForNyafazNativeBridge(timeoutMs = 12000) {
  return new Promise((resolve) => {
    if (isNyafazNativeMicroscopeAvailable()) {
      resolve(true);
      return;
    }
    const deadline = Date.now() + timeoutMs;
    const tick = () => {
      if (isNyafazNativeMicroscopeAvailable()) {
        resolve(true);
        return;
      }
      if (Date.now() >= deadline) {
        resolve(false);
        return;
      }
      window.setTimeout(tick, 100);
    };
    tick();
  });
}

export function supportsNativeInlineScalpPreview() {
  return (
    typeof window !== 'undefined' &&
    typeof window.NyafazNative?.startScalpInlinePreview === 'function' &&
    typeof window.NyafazNative?.captureScalpInline === 'function'
  );
}

/**
 * Ask Salon APK host to show Android camera permission if the bridge supports it.
 * Does NOT request USB/UVC permission — that steals ExtCam on Rockchip (breaks face cam).
 * @returns {Promise<boolean>}
 */
export async function requestNativeCameraPermission() {
  if (typeof window === 'undefined') return false;
  const native = window.NyafazNative;
  if (!native) return false;
  try {
    if (typeof native.requestCameraPermission === 'function') {
      await Promise.resolve(native.requestCameraPermission());
    } else if (typeof native.requestPermissions === 'function') {
      await Promise.resolve(native.requestPermissions(['camera']));
    }
    return true;
  } catch {
    return false;
  }
}

/**
 * Request USB microscope permission only (macro / scalp). Call from macro pages, not face scan.
 * @returns {Promise<boolean>}
 */
export async function requestNativeUsbPermission() {
  if (typeof window === 'undefined') return false;
  const native = window.NyafazNative;
  if (!native || typeof native.requestUsbPermission !== 'function') return false;
  try {
    await Promise.resolve(native.requestUsbPermission());
    return true;
  } catch {
    return false;
  }
}

/**
 * @param {unknown} detail
 * @returns {string|null}
 */
export function normalizeMicroscopeCaptureDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === 'string') {
    const s = detail.trim();
    if (s.startsWith('data:image/')) return s;
    return null;
  }
  if (typeof detail === 'object') {
    const o = /** @type {{ dataUrl?: string, base64?: string, mimeType?: string, error?: string }} */ (detail);
    if (typeof o.error === 'string' && o.error) {
      throw new Error(o.error);
    }
    if (typeof o.dataUrl === 'string' && o.dataUrl.startsWith('data:')) {
      return o.dataUrl;
    }
    if (typeof o.base64 === 'string' && o.base64.length > 0) {
      const mime = o.mimeType || 'image/jpeg';
      return `data:${mime};base64,${o.base64}`;
    }
  }
  return null;
}

/**
 * @param {HTMLElement|null|undefined} el
 */
export function measurePreviewRect(el) {
  if (!el || typeof el.getBoundingClientRect !== 'function') return null;
  const r = el.getBoundingClientRect();
  if (!(r.width > 8 && r.height > 8)) return null;
  return {
    left: Math.round(r.left),
    top: Math.round(r.top),
    width: Math.round(r.width),
    height: Math.round(r.height),
  };
}

/**
 * Start native USB live preview over the web preview frame.
 * @param {{
 *   target?: string,
 *   label?: string,
 *   rect?: {left:number,top:number,width:number,height:number}|null,
 *   nativeId?: number|string|null,
 *   usbDeviceId?: number|string|null,
 *   deviceId?: number|string|null,
 *   preferExternal?: boolean,
 * }} [opts]
 */
export function startNativeScalpInlinePreview(opts = {}) {
  if (!isNyafazNativeMicroscopeAvailable()) {
    throw new Error('Native USB microscope not available');
  }
  const payload = {
    target: opts.target || 'scalp_detail',
    label: opts.label || 'Scalp detail',
    preferExternal: opts.preferExternal !== false,
    ...(opts.rect || {}),
  };
  const nativeId = opts.nativeId ?? opts.usbDeviceId ?? opts.deviceId;
  if (nativeId != null && String(nativeId).trim() !== '') {
    payload.nativeId = nativeId;
  }
  if (typeof window.NyafazNative.startScalpInlinePreview === 'function') {
    window.NyafazNative.startScalpInlinePreview(payload);
    return;
  }
}

/**
 * @param {{left:number,top:number,width:number,height:number}|null} rect
 */
export function updateNativeScalpInlineRect(rect) {
  if (!rect || typeof window.NyafazNative?.updateScalpInlineRect !== 'function') return;
  window.NyafazNative.updateScalpInlineRect(rect);
}

export function stopNativeScalpInlinePreview() {
  if (typeof window.NyafazNative?.stopScalpInlinePreview === 'function') {
    window.NyafazNative.stopScalpInlinePreview();
  }
}

/**
 * List UVC cameras from the Salon APK bridge (UsbManager), not WebView Camera2.
 * @param {number} [timeoutMs]
 * @returns {Promise<Array<{ nativeId: number, label: string, vendorId?: number, productId?: number }>>}
 */
export function listNativeUsbCameras(timeoutMs = 5000) {
  return new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof window.NyafazNative?.listUsbCameras !== 'function') {
      resolve([]);
      return;
    }
    let settled = false;
    const finish = (cameras) => {
      if (settled) return;
      settled = true;
      window.removeEventListener('nyafaz-usb-cameras', onEvent);
      if (window.onNyafazUsbCameras === onCallback) {
        window.onNyafazUsbCameras = prev;
      }
      clearTimeout(timer);
      resolve(Array.isArray(cameras) ? cameras : []);
    };
    const normalize = (detail) => {
      if (Array.isArray(detail)) return detail;
      if (detail && Array.isArray(detail.cameras)) return detail.cameras;
      return [];
    };
    const onEvent = (e) => finish(normalize(e?.detail));
    const onCallback = (detail) => finish(normalize(detail));
    const prev = window.onNyafazUsbCameras;
    const timer = setTimeout(() => finish([]), timeoutMs);
    window.addEventListener('nyafaz-usb-cameras', onEvent);
    window.onNyafazUsbCameras = onCallback;
    try {
      window.NyafazNative.listUsbCameras();
    } catch {
      finish([]);
    }
  });
}

/**
 * Prefer USB MIC / XCX dermatoscope over generic HD / face UVC webcams.
 * @param {Array<{ nativeId?: number, label?: string, vendorId?: number, productId?: number }>} cameras
 * @returns {number|null}
 */
function nativeUsbEntryId(c) {
  const raw = c?.nativeId ?? c?.deviceId ?? c?.id;
  if (raw == null) return '';
  return String(raw).trim();
}

export function pickNativeUsbMicroscopeId(cameras) {
  const list = Array.isArray(cameras) ? cameras : [];
  if (!list.length) return null;
  const blob = (c) =>
    `${c?.label || ''} ${c?.vendorId || ''}:${c?.productId || ''}`.toLowerCase();
  const micro = list.filter((c) => /usb\s*mic|xcx|microscope|dermatoscope|macro|scalp/.test(blob(c)));
  if (micro.length) {
    const id = nativeUsbEntryId(micro[micro.length - 1]);
    return id || null;
  }
  const nonFace = list.filter((c) => !/hd\s*camera|emeet|logitech|webcam|ilitek/.test(blob(c)));
  const pick = nonFace.length ? nonFace[nonFace.length - 1] : list[list.length - 1];
  const id = nativeUsbEntryId(pick);
  return id || null;
}

function waitForNativeCapture(timeoutMs) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const prevCallback = window.onNyafazMicroscopeCapture;

    const cleanup = () => {
      window.removeEventListener('nyafaz-microscope-capture', onEvent);
      window.removeEventListener('nyafaz-microscope-error', onError);
      if (window.onNyafazMicroscopeCapture === onCallback) {
        window.onNyafazMicroscopeCapture = prevCallback;
      }
      clearTimeout(timer);
    };

    const finishOk = (dataUrl) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(dataUrl);
    };

    const finishErr = (err) => {
      if (settled) return;
      settled = true;
      cleanup();
      reject(err instanceof Error ? err : new Error(String(err)));
    };

    const handleDetail = (detail) => {
      try {
        const dataUrl = normalizeMicroscopeCaptureDetail(detail);
        if (!dataUrl) {
          finishErr(new Error('Microscope returned an empty or unreadable image'));
          return;
        }
        finishOk(dataUrl);
      } catch (e) {
        finishErr(e);
      }
    };

    const onEvent = (e) => handleDetail(e?.detail);
    const onCallback = (detail) => handleDetail(detail);
    const onError = (e) => {
      const msg = e?.detail?.error || 'USB microscope capture failed';
      finishErr(new Error(msg));
    };

    const timer = setTimeout(() => {
      finishErr(new Error('USB microscope capture timed out'));
    }, timeoutMs);

    window.addEventListener('nyafaz-microscope-capture', onEvent);
    window.addEventListener('nyafaz-microscope-error', onError);
    window.onNyafazMicroscopeCapture = onCallback;
  });
}

/**
 * Snap from the active inline USB preview (preferred) or legacy sheet.
 * @param {{ target?: string, label?: string, timeoutMs?: number, preferInline?: boolean }} [opts]
 * @returns {Promise<string>}
 */
export async function captureNativeMicroscopeInline(opts = {}) {
  // Custom Android OEMs often never fire the capture event — keep timeout short
  // so the page can fall back to WebView getUserMedia instead of hanging.
  const timeoutMs = opts.timeoutMs ?? 20000;
  if (!isNyafazNativeMicroscopeAvailable()) {
    throw new Error('Native USB microscope not available');
  }

  const wait = waitForNativeCapture(timeoutMs);

  try {
    if (
      opts.preferInline !== false &&
      typeof window.NyafazNative.captureScalpInline === 'function'
    ) {
      window.NyafazNative.captureScalpInline();
    } else if (typeof window.NyafazNative.captureMicroscope === 'function') {
      window.NyafazNative.captureMicroscope();
    } else if (typeof window.NyafazNative.openScalpCapture === 'function') {
      window.NyafazNative.openScalpCapture(opts.target || 'scalp_detail', opts.label || 'Scalp detail');
    } else {
      window.NyafazNative.openMicroscope();
    }
  } catch (e) {
    throw e instanceof Error ? e : new Error(String(e));
  }

  return wait;
}

/** @deprecated use captureNativeMicroscopeInline */
export function openNativeMicroscopeCapture(opts) {
  return captureNativeMicroscopeInline(opts);
}

/** Human labels for hair + skin macro targets shown in native chrome. */
export function nativeTargetDisplayName(target) {
  const key = String(target || '').trim().toLowerCase();
  const map = {
    hairline: 'Hairline',
    top: 'Top',
    crown: 'Crown',
    scalp_detail: 'Scalp detail',
    left_temple: 'Left temple',
    right_temple: 'Right temple',
    forehead: 'Forehead',
    cheek: 'Cheek',
    left_cheek: 'Left cheek',
    right_cheek: 'Right cheek',
  };
  return map[key] || (key ? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Detail');
}
